import React from "react";
import { Icon } from "./Icon";

const ICON = { sm: 16, md: 18, lg: 20 };

/**
 * The one button (REG-A, 2026-10-28; docs/BUTTONS_AUDIT.md, approved by Jorge).
 *
 * Hierarchy: `primary` (gold, dark label — ONE per screen) › `secondary` (dark
 * outline) › `ghost` › `link`. `master`, `trust` and `danger` are
 * special-purpose and never sit beside a primary.
 *
 * Styling is the `.fx-btn*` classes in index.css, read from the `--btn-*`
 * tokens in fixydany.css. It used to be inline style objects with JS hover,
 * which is why the focus ring never showed on a primary (an inline
 * box-shadow beat `:focus-visible`) and why a hover stuck after a tap.
 *
 *   - 48px minimum on phones at every size (`sm` is 40px only from 768px).
 *   - `loading` shows a spinner and KEEPS the label; callers pass the same
 *     label in both states, so the button never changes width.
 *   - `disabled` is grey, not a paler gold.
 *   - `leading` takes a node for a glyph that is not an `Icon` (Google's G).
 *   - `type` is left to the caller: a <Button> in a form still submits on
 *     Enter, as it always did.
 */
export const Button = React.forwardRef(function Button({
  variant = "primary", size = "md", icon, leading, iconRight, loading = false,
  disabled = false, fullWidth = false, as: El = "button", className = "",
  children, ...rest
}, ref) {
  const isButton = El === "button";
  const cls = [
    "fx-btn", `fx-btn--${variant}`, size !== "md" && `fx-btn--${size}`,
    fullWidth && "fx-btn--block", className,
  ].filter(Boolean).join(" ");
  const iconSize = ICON[size] || ICON.md;
  const lead = loading
    ? <Icon name="loader-2" size={iconSize} className="fx-btn__spin" aria-hidden="true" />
    : leading ?? (icon ? <Icon name={icon} size={iconSize} aria-hidden="true" /> : null);
  return (
    <El
      ref={ref}
      className={cls}
      disabled={isButton ? disabled : undefined}
      aria-disabled={(!isButton && disabled) || loading || undefined}
      aria-busy={loading || undefined}
      tabIndex={!isButton && disabled ? -1 : undefined}
      {...rest}
    >
      {lead}
      {children}
      {iconRight && !loading ? <Icon name={iconRight} size={iconSize} aria-hidden="true" /> : null}
    </El>
  );
});
